import React, { useState, useEffect } from 'react';

/**
 * AccessibilityPanel - A component that adds essential accessibility features to the website
 * This includes font size adjustments, contrast options, and focus helpers
 */
const AccessibilityPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [highContrast, setHighContrast] = useState(false);
  const [darkMode, setDarkMode] = useState(false); // Add dark mode state
  const [fontSize, setFontSize] = useState(0); // 0 = normal, 1 = large, 2 = larger
  const [reducedMotion, setReducedMotion] = useState(false);
  const [dyslexicFont, setDyslexicFont] = useState(false);

  // Load saved preferences from localStorage
  useEffect(() => {
    const loadSavedPreferences = () => {
      // Get saved contrast preference
      const savedHighContrast = localStorage.getItem('a11y-high-contrast');
      if (savedHighContrast === 'true') {
        setHighContrast(true);
      }
      
      // Get saved dark mode preference
      const savedDarkMode = localStorage.getItem('a11y-dark-mode');
      if (savedDarkMode === 'true') {
        setDarkMode(true);
      }
      
      // Get saved font size preference
      const savedFontSize = localStorage.getItem('a11y-font-size');
      if (savedFontSize) {
        setFontSize(parseInt(savedFontSize, 10));
      }
      
      // Get saved motion preference
      const savedReducedMotion = localStorage.getItem('a11y-reduced-motion');
      if (savedReducedMotion === 'true') {
        setReducedMotion(true);
      }
      
      // Get saved dyslexic font preference
      const savedDyslexicFont = localStorage.getItem('a11y-dyslexic-font');
      if (savedDyslexicFont === 'true') {
        setDyslexicFont(true);
      }
    };
    
    loadSavedPreferences();
  }, []);

  // Apply the high contrast mode
  useEffect(() => {
    if (highContrast) {
      document.body.classList.add('high-contrast-mode');
      // Remove dark mode if high contrast is enabled
      document.body.classList.remove('dark-mode');
      setDarkMode(false);
    } else {
      document.body.classList.remove('high-contrast-mode');
      // Re-apply dark mode if it was active
      if (localStorage.getItem('a11y-dark-mode') === 'true') {
        document.body.classList.add('dark-mode');
        setDarkMode(true);
      }
    }
    
    localStorage.setItem('a11y-high-contrast', highContrast.toString());
  }, [highContrast]);

  // Apply dark mode
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
      // Remove high contrast if dark mode is enabled
      document.body.classList.remove('high-contrast-mode');
      setHighContrast(false);
    } else {
      document.body.classList.remove('dark-mode');
      // Re-apply high contrast if it was active
      if (localStorage.getItem('a11y-high-contrast') === 'true') {
        document.body.classList.add('high-contrast-mode');
        setHighContrast(true);
      }
    }
    
    localStorage.setItem('a11y-dark-mode', darkMode.toString());
  }, [darkMode]);

  // Apply the font size adjustment
  useEffect(() => {
    document.body.classList.remove('font-size-large', 'font-size-larger');
    
    if (fontSize === 1) {
      document.body.classList.add('font-size-large');
    } else if (fontSize === 2) {
      document.body.classList.add('font-size-larger');
    }
    
    localStorage.setItem('a11y-font-size', fontSize.toString());
  }, [fontSize]);

  // Apply reduced motion preference
  useEffect(() => {
    if (reducedMotion) {
      document.body.classList.add('reduced-motion');
    } else {
      document.body.classList.remove('reduced-motion');
    }
    
    localStorage.setItem('a11y-reduced-motion', reducedMotion.toString());
  }, [reducedMotion]);

  // Apply dyslexic friendly font
  useEffect(() => {
    if (dyslexicFont) {
      document.body.classList.add('dyslexic-font');
    } else {
      document.body.classList.remove('dyslexic-font');
    }
    
    localStorage.setItem('a11y-dyslexic-font', dyslexicFont.toString());
  }, [dyslexicFont]);

  // Toggle the panel open/closed
  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  // Handle changing font size
  const handleFontSize = (sizeIndex) => {
    setFontSize(sizeIndex);
  };

  return (
    <div className="accessibility-tools fixed z-50 right-5 top-1/2 transform -translate-y-1/2">
      {/* Accessibility Toggle Button */}
      <button
        onClick={togglePanel}
        className="bg-base-green text-white p-3 rounded-l-lg shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-base-green"
        aria-expanded={isOpen}
        aria-label="Accessibility options"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      </button>

      {/* Accessibility Panel */}
      <div 
        className={`bg-white border border-gray-200 rounded-lg shadow-lg absolute right-16 top-1/2 transform -translate-y-1/2 w-64 p-4 transition-transform duration-300 ${
          isOpen ? 'translate-x-0 opacity-100' : 'translate-x-96 opacity-0 pointer-events-none'
        }`}
        aria-hidden={!isOpen}
      >
        <h2 className="text-lg font-semibold mb-3 text-gray-800">Accessibility Options</h2>
        
        <div className="space-y-4">
          {/* Dark Mode Toggle */}
          <div>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={darkMode}
                onChange={() => setDarkMode(!darkMode)}
                aria-label="Enable dark mode"
              />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-base-green rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-base-green"></div>
              <span className="ml-3 text-sm font-medium text-gray-700">Dark Mode</span>
            </label>
          </div>

          {/* High Contrast Toggle */}
          <div>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={highContrast}
                onChange={() => setHighContrast(!highContrast)}
                aria-label="Enable high contrast mode"
              />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-base-green rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-base-green"></div>
              <span className="ml-3 text-sm font-medium text-gray-700">High Contrast</span>
            </label>
          </div>
          
          {/* Reduced Motion Toggle */}
          <div>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={reducedMotion}
                onChange={() => setReducedMotion(!reducedMotion)}
                aria-label="Reduce motion effects"
              />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-base-green rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-base-green"></div>
              <span className="ml-3 text-sm font-medium text-gray-700">Reduce Motion</span>
            </label>
          </div>
          
          {/* Dyslexic Font Toggle */}
          <div>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={dyslexicFont}
                onChange={() => setDyslexicFont(!dyslexicFont)}
                aria-label="Use dyslexia-friendly font"
              />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-base-green rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-base-green"></div>
              <span className="ml-3 text-sm font-medium text-gray-700">Dyslexic Font</span>
            </label>
          </div>
          
          {/* Font Size Controls */}
          <div className="mt-3">
            <p className="text-sm font-medium text-gray-700 mb-2">Text Size:</p>
            <div className="flex space-x-2">
              <button
                onClick={() => handleFontSize(0)}
                className={`px-3 py-1 rounded ${
                  fontSize === 0 ? 'bg-base-green text-white' : 'bg-gray-100 text-gray-700'
                }`}
                aria-label="Normal text size"
                aria-pressed={fontSize === 0}
              >
                A
              </button>
              <button
                onClick={() => handleFontSize(1)}
                className={`px-3 py-1 rounded text-lg ${
                  fontSize === 1 ? 'bg-base-green text-white' : 'bg-gray-100 text-gray-700'
                }`}
                aria-label="Large text size"
                aria-pressed={fontSize === 1}
              >
                A
              </button>
              <button
                onClick={() => handleFontSize(2)}
                className={`px-3 py-1 rounded text-xl ${
                  fontSize === 2 ? 'bg-base-green text-white' : 'bg-gray-100 text-gray-700'
                }`}
                aria-label="Larger text size"
                aria-pressed={fontSize === 2}
              >
                A
              </button>
            </div>
          </div>
          
          <button
            onClick={() => {
              setHighContrast(false);
              setDarkMode(false);
              setFontSize(0);
              setReducedMotion(false);
              setDyslexicFont(false);
            }}
            className="w-full mt-4 py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded transition duration-200"
            aria-label="Reset all accessibility settings"
          >
            Reset All
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityPanel;